import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import {
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Dialog
} from '@material-ui/core'
import InputMask from 'react-input-mask'
import { Save, ArrowDropDown } from '@material-ui/icons'
import { FilterComponent } from '~/views/shared'
import { useStyles } from './AccountTransferForm.style'

const validationSchema = Yup.object().shape({
  Cuit: Yup.string()
    .trim()
    .required('Es requerido ingresar el Cuit')
    .max(20, 'La cantidad máxima es de 20 caracteres'),
  RazonSocial: Yup.string()
    .trim()
    .required('Es requerido ingresar la razón social')
    .max(300, 'La cantidad máxima es de 300 caracteres'),
  Email: Yup.string()
    .trim()
    .email('Debe ser un email válido')
    .required('Es requerido ingresar el email')
    .max(200, 'La cantidad máxima es de 200 caracteres'),
  Name: Yup.string()
    .trim()
    .required('Es requerido ingresar el nombre')
    .max(250, 'La cantidad máxima es de 250 caracteres'),
  BankAccountIdentifier: Yup.string()
    .trim()
    .required('Es requerido ingresar el CBU o CVU')
    .matches(/^\d{22}$/, 'El CBU debe tener 22 dígitos numéricos'),
  BankAccountAlias: Yup.string()
    .trim()
    .required('Es requerido ingresar el alias')
    .matches(
      /^[a-zA-Z0-9.]{6,20}$/,
      'El alias debe tener entre 6 y 20 caracteres, solo letras, números o puntos'
    ),
  BankName: Yup.string()
    .trim()
    .required('Es requerido ingresar el nombre del banco o billetera virtual')
    .max(100, 'La cantidad máxima es de 100 caracteres'),
  Organizers: Yup.array()
    .min(1, 'Es requerido seleccionar al menos una productora')
    .required('Es requerido seleccionar al menos una productora')
})

export const AccountTransferForm = ({
  values,
  organizers,
  actionText,
  handleSubmit,
  onCancel,
  pendingOrganizers,
  pendingCreate
}) => {
  const classes = useStyles()
  const [openOrganizer, setOpenOrganizer] = useState(false)
  const [organizersSelected, setOrganizersSelected] = useState(null)
  const [organizerIds, setOrganizerIds] = useState(values.Organizers || [])
  const [searchValueOrganizer, setSearchValueOrganizer] = useState('')
  const [organizersFilter, setOrganizersFilter] = useState(organizers)

  const trimFormValues = (values, fields) => {
    fields.forEach((field) => {
      if (typeof values[field] === 'string') {
        values[field] = values[field].trim()
      }
    })
    return values
  }

  const handleClickOpenOrganizers = () => {
    setSearchValueOrganizer('')
    setOrganizersFilter(organizers)
    setOpenOrganizer(true)
  }

  const performSearchOrganizers = (searchKey) => {
    setSearchValueOrganizer(searchKey)
    setOrganizersFilter(
      organizers?.filter((e) =>
        e?.Name?.toUpperCase().includes(searchKey.toUpperCase())
      )
    )
  }

  const handleClickSelect = (e) => {
    const organizerId = e.Id
    const isSelected = organizerIds.includes(organizerId)

    const updatedOrganizerIds = isSelected
      ? organizerIds.filter((id) => id !== organizerId)
      : [...organizerIds, organizerId]

    setOrganizerIds(updatedOrganizerIds)

    const updatedOrganizerTitles = organizers
      .filter((organizer) => updatedOrganizerIds.includes(organizer.Id))
      .map((organizer) => organizer.Name)

    setOrganizersSelected(
      updatedOrganizerTitles.join('\u00A0\u00A0\u00A0|\u00A0\u00A0\u00A0')
    )
  }

  useEffect(() => {
    return () => {
      setOrganizersSelected(null)
      setOrganizerIds([])
    }
  }, [])

  return (
    <Formik
      enableReinitialize
      initialValues={values}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.fieldRow}>
              <InputMask
                mask='99-99999999-9'
                value={values.Cuit}
                onChange={handleChange}
                onBlur={handleBlur}>
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label={
                      (errors.Cuit && touched.Cuit && errors.Cuit) || 'Cuit'
                    }
                    name='Cuit'
                    error={touched.Cuit && Boolean(errors.Cuit)}
                    fullWidth
                    required
                    size='small'
                    variant='outlined'
                    className={classes.field}
                  />
                )}
              </InputMask>
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.RazonSocial &&
                    touched.RazonSocial &&
                    errors.RazonSocial) ||
                  'Razón Social'
                }
                name='RazonSocial'
                value={values.RazonSocial}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.RazonSocial && Boolean(errors.RazonSocial)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Email && touched.Email && errors.Email) || 'Email'
                }
                name='Email'
                value={values.Email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Email && Boolean(errors.Email)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={(errors.Name && touched.Name && errors.Name) || 'Nombre'}
                name='Name'
                value={values.Name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Name && Boolean(errors.Name)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.BankAccountIdentifier &&
                    touched.BankAccountIdentifier &&
                    errors.BankAccountIdentifier) ||
                  'CBU / CVU'
                }
                name='BankAccountIdentifier'
                value={values.BankAccountIdentifier}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.BankAccountIdentifier &&
                  Boolean(errors.BankAccountIdentifier)
                }
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.BankAccountAlias &&
                    touched.BankAccountAlias &&
                    errors.BankAccountAlias) ||
                  'Alias'
                }
                name='BankAccountAlias'
                value={values.BankAccountAlias}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.BankAccountAlias && Boolean(errors.BankAccountAlias)
                }
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.BankName && touched.BankName && errors.BankName) ||
                  'Banco o Billetera Virtual'
                }
                name='BankName'
                value={values.BankName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.BankName && Boolean(errors.BankName)}
                fullWidth
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>

            {/* Selección de Productoras */}
            <FormControl
              variant='outlined'
              className={`${classes.fieldRow} ${classes.fieldRowSelect}`}>
              <InputLabel shrink={true} required>
                {(errors.Organizers &&
                  touched.Organizers &&
                  errors.Organizers) ||
                  'Productoras'}
              </InputLabel>
              <Button
                disableFocusRipple
                onClick={handleClickOpenOrganizers}
                variant='outlined'
                disabled={pendingOrganizers}
                className={classes.textButton}>
                <div className={classes.containerSelectButton}>
                  <span
                    style={
                      !organizersSelected ||
                      (Array.isArray(organizersSelected) &&
                        organizersSelected.length === 0)
                        ? { color: 'gray' }
                        : { color: 'black' }
                    }>
                    {organizersSelected && organizersSelected.length > 0
                      ? organizersSelected
                      : pendingOrganizers
                        ? 'Cargando...'
                        : 'Seleccione una o más productoras'}
                  </span>
                  {pendingOrganizers ? (
                    <CircularProgress size={24} color='secondary' />
                  ) : (
                    <ArrowDropDown />
                  )}
                </div>
              </Button>
              <Dialog
                open={openOrganizer}
                onClose={() => setOpenOrganizer(false)}>
                <div className={classes.containerDialog}>
                  <div>
                    <FilterComponent
                      performSearch={performSearchOrganizers}
                      searchValue={searchValueOrganizer}
                      placeholder='Ingrese Nombre de la productora...'
                    />
                  </div>
                  <Select
                    multiple
                    native
                    labelId='organizer-label'
                    name='Organizers'
                    id='Organizers-select'
                    value={organizerIds}
                    onChange={handleChange}
                    className={classes.fieldSelect}>
                    {organizersFilter &&
                      organizersFilter.map((o) => (
                        <option
                          key={o.Id}
                          className={classes.items}
                          value={o.Id}
                          onClick={() => handleClickSelect(o)}>
                          {o.Name}
                        </option>
                      ))}
                  </Select>
                  <Button
                    onClick={() => setOpenOrganizer(false)}
                    color='primary'
                    className={classes.dialogButton}
                    variant='contained'>
                    OK
                  </Button>
                </div>
              </Dialog>
            </FormControl>
          </div>

          <div className={classes.actionsContainer}>
            <Button
              onClick={onCancel}
              disabled={isSubmitting}
              autoFocus={false}
              color='primary'>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                values.Organizers = organizerIds.map((id) => ({
                  OrganizerId: id
                }))
                trimFormValues(values, [
                  'Name',
                  'Cuit',
                  'RazonSocial',
                  'Email',
                  'BankAccountAlias',
                  'BankAccountIdentifier',
                  'BankName'
                ])
                handleSubmit()
              }}
              disabled={pendingOrganizers || pendingCreate || isSubmitting}
              color='primary'
              variant='contained'
              startIcon={<Save />}
              autoFocus={false}>
              {pendingOrganizers || pendingCreate || isSubmitting ? (
                <CircularProgress size={24} color='secondary' />
              ) : (
                actionText
              )}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default AccountTransferForm
